// Dependencies
// -----------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { postWlsIdPhoto, deleteWlsIdPhoto } from '../../utils/wls-api-client';
import Dropzone from 'react-dropzone';
import styled from 'styled-components';

// Components
// -----------------------------------------------
import {
  Box,
  CallToActionButton,
  IconFontAwesome
} from '@directsoftware/ui-kit-web-admin';
import displayError from '../errors/display';
import Ripple from '../miscellaneous/ripple';
import { Delete } from '../listings/resources/icons';

// Constants
// -----------------------------------------------
const MAX_IMAGE_DIMENSION = 1600;
const COMPRESSED_IMAGE_QUALITY = 0.8;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Styles
// -----------------------------------------------
const DropContainer = styled.div`
  border: 4px dashed #e5e2e2;
  padding: 16px;
  text-align: center;
`;

const SizeHint = styled.div`
  color: #767676;
  font-size: 12px;
  margin-top: 8px;
`;

// -----------------------------------------------
// COMPONENT->ID-PHOTO-UPLOADER ------------------
// -----------------------------------------------
class IdPhotoUploader extends React.Component {
  // Constructor
  // ---------------------------------------------
  constructor(props) {
    super(props);

    this.state = {
      isLoaded: false,
      isLoading: false,
      idPhoto: null
    };
  }

  // On Dropzone Drop
  // ---------------------------------------------
  onDropzoneDrop = (acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length) {
      this.attemptUploadFile(acceptedFiles[0]);
    }
  };

  // Read File as Data URL
  // ---------------------------------------------
  readFileAsDataUrl = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  // Downscale a data URL so the base64 payload stays well under the request
  // size limits. Returns the original data URL if the image cannot be decoded.
  // ---------------------------------------------
  downscaleDataUrl = dataUrl =>
    new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        const scale = Math.min(
          1,
          MAX_IMAGE_DIMENSION / Math.max(image.width, image.height)
        );
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(dataUrl);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', COMPRESSED_IMAGE_QUALITY));
      };
      image.onerror = () => resolve(dataUrl);
      image.src = dataUrl;
    });

  // Read File as base64
  // ---------------------------------------------
  readFileAsBase64 = file =>
    this.readFileAsDataUrl(file)
      .then(this.downscaleDataUrl)
      // Strip the "data:<mime>;base64," prefix — the WLS endpoint expects
      // the raw base64 payload (it runs base64.b64decode on it).
      .then(dataUrl => dataUrl.split(',')[1] || '');

  // Attempt Upload File
  // ---------------------------------------------
  attemptUploadFile = file_queued => {
    if (file_queued.size > MAX_FILE_SIZE_BYTES) {
      displayError({
        message: `That photo is larger than the ${MAX_FILE_SIZE_MB}MB limit — please try a smaller image.`
      });
      return;
    }

    this.setState({ isLoaded: false, isLoading: true }, () => {
      if (this.props.localOnly) {
        this.readFileAsDataUrl(file_queued)
          .then(this.downscaleDataUrl)
          .then(dataUri => {
            const idPhoto = { url: dataUri, data_uri: dataUri };
            this.setState({ isLoading: false, idPhoto }, () => {
              this.props.afterUpload && this.props.afterUpload(idPhoto);
            });
          })
          .catch(() => {
            this.setState({ isLoading: false });
            displayError({
              message: 'Unable to read photo. Please try again.'
            });
          });
        return;
      }
      this.readFileAsBase64(file_queued)
        .then(document => {
          const data = {
            document,
            booking_code: this.props.booking.booking_code,
            organization_id: this.props.booking.organization_id
          };

          if (this.props.idSide) {
            data.id_side = this.props.idSide;
          }

          return postWlsIdPhoto(this.props.brand, data);
        })
        .then(response => {
          this.setState(
            {
              isLoaded: true,
              isLoading: false,
              idPhoto: response.data.id_photo
            },
            () =>
              this.props.afterUpload &&
              this.props.afterUpload(response.data.id_photo)
          );
        })
        .catch(error => {
          this.setState({
            isLoaded: false,
            isLoading: false
          });
          const status = error.response && error.response.status;
          displayError({
            message:
              status === 413
                ? 'That photo is too large to upload. Please try a smaller image.'
                : 'Error uploading photo',
            err: error.response || {},
            show4XX: true
          });
        });
    });
  };

  // Delete ID Photo
  // ---------------------------------------------
  deleteIdPhoto = () => {
    if (this.props.localOnly) {
      this.setState({ idPhoto: null }, () => {
        this.props.afterPhotoDelete && this.props.afterPhotoDelete();
      });
      return;
    }
    deleteWlsIdPhoto(this.props.brand, this.state.idPhoto.id, this.props.booking.booking_code)
      .then(response => {
        this.setState({ idPhoto: null }, () => {
          this.props.afterPhotoDelete && this.props.afterPhotoDelete();
        });
      })
      .catch(error => {
        displayError({
          message: 'Error deleting photo',
          err: error.response || {},
          show4XX: true
        });
      });
  };

  // Render Loading
  // ---------------------------------------------
  renderLoading = () => {
    return <Ripple color="#50E3C2" />;
  };

  // Render Viewing
  // ---------------------------------------------
  renderViewing = () => {
    if (this.state.idPhoto) {
      return (
        <div style={{ position: 'relative' }}>
          <img
            src={this.state.idPhoto.url}
            style={{ width: '100%' }}
            alt="id-photo"
          />
          <Delete
            style={{
              position: 'absolute',
              right: '8px',
              bottom: '8px',
              cursor: 'pointer'
            }}
            onClick={this.deleteIdPhoto}
          />
        </div>
      );
    } else {
      return (
        <Box className="dropzone-card">
          <Dropzone
            activeClassName=""
            multiple={false}
            onDrop={this.onDropzoneDrop}
            ref={node => {
              this.dropzone = node;
            }}
            accept="image/*"
          >
            {({ getRootProps, getInputProps }) => (
              <DropContainer>
                <div className="dropzone-card" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <CallToActionButton
                    onClick={() => {}}
                    prependIcon={<IconFontAwesome name="upload" />}
                  >
                    Upload Driver's License/Passport
                  </CallToActionButton>
                  <SizeHint>{`JPG or PNG, up to ${MAX_FILE_SIZE_MB}MB`}</SizeHint>
                </div>
              </DropContainer>
            )}
          </Dropzone>
        </Box>
      );
    }
  };

  // Render
  // ---------------------------------------------
  render() {
    return (
      <section>
        {this.state.isLoading ? this.renderLoading() : this.renderViewing()}
      </section>
    );
  }
}

IdPhotoUploader.propTypes = {
  localOnly: PropTypes.bool,
  brand: PropTypes.object.isRequired,
  booking: PropTypes.object,
  idSide: PropTypes.string,
  afterUpload: PropTypes.func,
  afterPhotoDelete: PropTypes.func
};
IdPhotoUploader.defaultProps = {
  localOnly: false,
  booking: null,
  idSide: null,
  afterUpload: null,
  afterPhotoDelete: null
};

function mapStateToProps(state) {
  return { brand: state.brand };
}

export default connect(mapStateToProps)(IdPhotoUploader);
